import InfoRounded from '@mui/icons-material/InfoRounded';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { SwitchProps } from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { useTranslation } from 'react-i18next';
import { organizationFields } from '../../../../constants';

type PartnerFieldsProps = {
  areCuratedDealFeesEnabled: boolean;
  labels: Record<string, string>;
  onCuratedDealFeesSwitchChange: SwitchProps['onChange'];
  onExchangeAccountPartnerIdChange: (value: string) => void;
  shouldDisableCuratedDealFees: boolean;
};

export const PartnerFields = ({
  areCuratedDealFeesEnabled,
  labels,
  onCuratedDealFeesSwitchChange,
  onExchangeAccountPartnerIdChange,
  shouldDisableCuratedDealFees,
}: PartnerFieldsProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 2 }}>
      <SwitchField
        checked={areCuratedDealFeesEnabled}
        data-test="is-oa-access-enabled"
        label={t('Allow access to setting Curated Deal Fees on deals')}
        onChange={onCuratedDealFeesSwitchChange}
        disabled={shouldDisableCuratedDealFees}
      />

      {areCuratedDealFeesEnabled && (
        <IndentedSections>
          <RHFTextField
            dataTest="partner-id"
            fullWidth
            label={labels[organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID]}
            margin="dense"
            name={organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID}
            onChange={onExchangeAccountPartnerIdChange}
            placeholder={t('Enter Partner ID')}
          />

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              mb: 2,
            }}
          >
            <RHFCheckboxField
              data-test="curated-deal-buyer-discounts-checkbox"
              formControlLabelSx={{ marginInline: 0 }}
              label={labels[organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED]}
              margin="dense"
              name={organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED}
            />

            <Tooltip
              title={t(
                'A curated deal buyer discount is a potential option for a data partner to offer a discount from their fee to buyers.'
              )}
            >
              <IconButton>
                <InfoRounded />
              </IconButton>
            </Tooltip>
          </Stack>
        </IndentedSections>
      )}

      {shouldDisableCuratedDealFees && (
        <Alert severity="warning" sx={{ mb: 2, mt: 1, alignItems: 'center', maxWidth: 'fit-content' }}>
          <Typography>{t('Curated Deal Fees setting are disabled for sub-organizations.')}</Typography>
        </Alert>
      )}
    </Box>
  );
};
